import React, { useCallback, useEffect, useRef, useState } from 'react'
import { factories, IEmbedConfiguration, models, service } from 'powerbi-client'
import {
  Box,
  Button,
  FormControl,
  Select,
  SelectChangeEvent,
} from '@mui/material'
import { PbiReportViewModel } from '@rsmus/ecp-userservice'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faExpand } from '@fortawesome/free-solid-svg-icons'
import { useTranslation } from 'react-i18next'
import type { Report } from 'powerbi-client'
import { useNavigate } from 'react-router'
import { useDispatch } from 'react-redux'
import Spinner from '../forms/Spinner'
import Unavailable from '../../pages/Unavailable'
import { Styles } from '../../types'
import MainContent from '../layouts/MainContent'
import api from '../../api'
import tokens from '../../styles/tokens.json'
import { ArrowDownIcon } from '../../rsmCoreComponents/components/icons'
import { useDeviceType } from '../../rsmCoreComponents/hooks/useDeviceType'
import {
  setAdminSelectedReportId,
  setReportData,
} from '../../store/powerBI/reportSlice'

const MINUTES_BEFORE_EXPIRATION = 10 // 10 minutes before expiration to refresh token
const MIN_REFRESH_INTERVAL_MS = 60_000 // prevent tight refresh loops

// To convert hex to rgba for Shadow effect
const hexToRgba = (hex: string, alpha: number) =>
  `rgba(${parseInt(hex.slice(1, 3), 16)}, ${parseInt(
    hex.slice(3, 5),
    16,
  )}, ${parseInt(hex.slice(5, 7), 16)}, ${alpha})`

interface ReportEmbed {
  reportId: string
  embedUrl: string
  accessToken: string
  tokenExpiration: string
  isDashboard: boolean
  reportDisplayName: string
}

interface ReportData {
  reportId: string
  reportName: string | undefined
  reportDisplayName: string
  embedUrl: string
  accessToken: string
  tokenExpiration: string
  hasAdminRole: boolean
  pbiReports: PbiReportViewModel[] | undefined
}

const defaultReportData: ReportData = {
  reportId: '',
  reportName: '',
  reportDisplayName: '',
  embedUrl: '',
  accessToken: '',
  tokenExpiration: '',
  hasAdminRole: false,
  pbiReports: [],
}

const styles: Styles = {
  content: {
    backgroundColor: tokens.colors.white,
    justifyContent: 'center',
    height: 'fit-content',
  },
  fullscreenButtonContainer: {
    display: 'flex',
    justifyContent: 'flex-end',
    width: '100%',
  },
  fullscreenButton: (theme) => ({
    display: 'inline-flex',
    alignItems: 'center',
    fontWeight: 500,
    fontSize: '1rem',
    mb: 2,
    textTransform: 'none',
    padding: 0,
    minWidth: 0,
    color: theme.palette.secondary.main,
    textDecoration: 'underline',
    '& .MuiButton-startIcon': {
      marginRight: 1,
      color: tokens.colors.black,
    },
  }),
}

interface PowerBIReportProps {
  reportName: string | undefined
  onReportDisplayNameChange?: (name: string) => void
}

const PowerBIReport = ({
  reportName,
  onReportDisplayNameChange,
}: PowerBIReportProps) => {
  const { isMobile, isTablet } = useDeviceType()
  const reportDiv = useRef<HTMLDivElement>(null)
  const embeddedReportRef = useRef<Report | null>(null)
  const refreshTimeoutRef = useRef<number | undefined>(undefined)
  const isRefreshingRef = useRef(false)
  const { t } = useTranslation()
  const dispatch = useDispatch()
  const navigate = useNavigate()
  const [isLoaded, setIsLoaded] = useState<boolean>(false)
  const [tokenExpiration, setTokenExpiration] = useState<string>('')
  const [failedToRetrieveReport, setFailedToRetrieveReport] = useState(false)
  const [failedToRetrieveNewReport, setFailedToRetrieveNewReport] =
    useState(false)
  const [isAdminUser, setIsAdminUser] = useState<boolean>(false)
  const [adminSelectionEmpty, setIsAdminSelectionEmpty] =
    useState<boolean>(false)
  const [adminDropdownValue] = useState<number | undefined>(undefined)
  const [pbiReportsList, setPbiReportsList] = useState<
    PbiReportViewModel[] | undefined
  >([])

  const powerbi = new service.Service(
    factories.hpmFactory,
    factories.wpmpFactory,
    factories.routerFactory,
  )

  async function loadReport(
    embedUrl: string,
    accessToken: string,
    reportId: string,
  ) {
    if (embedUrl === '' || accessToken === '' || reportId === '') return
    const reportContainer = reportDiv.current

    if (reportContainer) {
      powerbi.reset(reportContainer)
    }

    if (reportContainer) {
      const embedConfig: IEmbedConfiguration = {
        type: 'report',
        id: reportId,
        embedUrl,
        accessToken,
        tokenType: models.TokenType.Embed,
      }

      const report = (await powerbi.embed(
        reportContainer,
        embedConfig,
      )) as Report
      embeddedReportRef.current = report
      report.setComponentTitle(
        `${reportName} ${t('PowerBI.Report.Report').toLowerCase()}`,
      )

      report.off('loaded')
      report.on('loaded', () => console.log('Report has loaded'))
    }
  }

  // Fetch report config from API
  async function getReportData(
    nameOfReport: string | undefined,
  ): Promise<ReportData> {
    try {
      const response = await api.user.report_GetReportClientByName(nameOfReport)
      const data = response?.data
      return {
        reportId: data?.embedReport?.reportId ?? '',
        reportName,
        reportDisplayName: data?.embedReport?.reportDisplayName ?? '',
        embedUrl: data?.embedReport?.embedUrl ?? '',
        accessToken: data?.embedReport?.accessToken ?? '',
        tokenExpiration: data?.embedReport?.tokenExpiration ?? '', // UTC
        hasAdminRole: data?.hasAdminRole ?? false,
        pbiReports: data?.pbiReports ?? [],
      }
    } catch (error) {
      setFailedToRetrieveReport(true)
      return defaultReportData
    }
  }

  async function getAdminUserReportData(
    id: number | undefined,
  ): Promise<ReportEmbed> {
    try {
      const response = await api.user.report_GetPbiEmdedReportById(id)
      const data = response?.data
      return {
        reportId: data?.reportId ?? '',
        embedUrl: data?.embedUrl ?? '',
        accessToken: data?.accessToken ?? '',
        tokenExpiration: data?.tokenExpiration ?? '', // UTC
        isDashboard: data?.isDashboard ?? false,
        reportDisplayName: data?.reportDisplayName ?? '',
      }
    } catch (error) {
      setFailedToRetrieveNewReport(true)
      return {
        reportId: '',
        embedUrl: '',
        accessToken: '',
        tokenExpiration: '',
        isDashboard: false,
        reportDisplayName: '',
      } as ReportEmbed
    }
  }

  // Refresh token and apply to embedded report
  async function updateToken() {
    if (isRefreshingRef.current) return

    isRefreshingRef.current = true
    const newAccessToken = await getReportData(reportName)
    isRefreshingRef.current = false

    if (
      newAccessToken.reportId.length === 0 ||
      newAccessToken.embedUrl.length === 0 ||
      newAccessToken.accessToken.length === 0
    ) {
      if (isAdminUser) {
        setFailedToRetrieveNewReport(true)
      } else {
        setFailedToRetrieveReport(true)
      }
    }
    const reportContainer = reportDiv.current

    if (reportContainer && newAccessToken) {
      const report = powerbi.get(reportContainer)
      const newExpiration = newAccessToken.tokenExpiration || ''
      const parsedExpiration = Date.parse(newExpiration)

      if (!newExpiration || Number.isNaN(parsedExpiration)) {
        // stop scheduling refresh attempts if the service does not return a usable expiration
        setTokenExpiration('')
        return
      }

      await report.setAccessToken(newAccessToken.accessToken || '')
      if (newExpiration !== tokenExpiration) {
        setTokenExpiration(newExpiration)
      }
    }
  }

  // Handle admin report dropdown change
  const handleAdminDropDownChange = async (
    event: SelectChangeEvent<number>,
  ) => {
    const reportId =
      event.target.value !== undefined ? Number(event.target.value) : 0
    if (reportId !== 0 && reportId !== adminDropdownValue) {
      setIsAdminSelectionEmpty(false)
      if (failedToRetrieveNewReport) {
        // if error state was set previously, reset it
        setFailedToRetrieveNewReport(false)
        setFailedToRetrieveReport(false)
      }
      setIsLoaded(false)
      try {
        if (isMobile) {
          dispatch(setAdminSelectedReportId(reportId))
          navigate(`/m/Report/${reportName}`)
        }

        const updatedReport = await getAdminUserReportData(reportId)
        onReportDisplayNameChange?.(updatedReport.reportDisplayName)
        await loadReport(
          updatedReport.embedUrl,
          updatedReport.accessToken,
          updatedReport.reportId,
        )
        if (
          updatedReport.embedUrl.length === 0 ||
          updatedReport.accessToken.length === 0 ||
          updatedReport.reportId.length === 0
        ) {
          setFailedToRetrieveNewReport(true)
        }
        setIsLoaded(true)
      } catch (error) {
        setFailedToRetrieveNewReport(true)
      }
    } else if (reportId === 0) {
      const reportContainer = reportDiv.current
      // reset report container if no report is selected
      if (reportContainer) {
        powerbi.reset(reportContainer)
      }
      setIsAdminSelectionEmpty(true)
      setFailedToRetrieveNewReport(false)
      setFailedToRetrieveReport(false)
      embeddedReportRef.current = null
    }
    setIsLoaded(true)
  }

  const handleFullscreen = () => {
    if (embeddedReportRef.current) {
      embeddedReportRef.current.fullscreen()
    }
  }

  const getDeviceWidth = () => {
    if (typeof window !== 'undefined') {
      const mainContent = document.querySelector(
        '.MainContent',
      ) as HTMLElement | null
      if (mainContent) {
        const padding = isTablet ? 64 : 208
        return mainContent.clientWidth - padding
      }
      return window.innerWidth
    }
    return 0
  }

  const getReportHeight = useCallback(() => {
    if (failedToRetrieveNewReport || adminSelectionEmpty) {
      return '0rem'
    }
    return isMobile || isTablet ? '40rem' : '51.25rem'
  }, [failedToRetrieveNewReport, adminSelectionEmpty, isTablet, isMobile])

  const getReportContainerStyle = useCallback(() => {
    const style: React.CSSProperties = {}
    style.display = 'flex'
    style.backgroundColor = tokens.colors.backgroundGray
    style.borderRadius = '0.5rem'
    style.overflow = 'hidden'
    style.position = 'relative'
    style.boxShadow = `0 0.125rem 0.625rem ${hexToRgba(
      tokens.colors.black,
      0.1,
    )}`
    style.maxWidth = getDeviceWidth()
    style.minWidth = isTablet ? 'calc(100vw - 4rem)' : 'calc(100vw - 13rem)'
    style.height = getReportHeight()
    style.width = isTablet ? 'calc(100vw - 4rem)' : '100%'

    return style
  }, [isMobile, isTablet, getDeviceWidth, getReportHeight])

  // Initial fetch on mount
  useEffect(() => {
    const getReportInfo = async () => {
      setIsLoaded(false)
      const report = await getReportData(reportName)
      onReportDisplayNameChange?.(report.reportDisplayName)
      if (isMobile) {
        dispatch(setReportData(report))
      }

      if (report.hasAdminRole) {
        setIsAdminUser(report.hasAdminRole)
        setIsAdminSelectionEmpty(true)
        setPbiReportsList(report.pbiReports)
        setIsLoaded(true)
        return
      }

      if (!isMobile) {
        setTokenExpiration(report.tokenExpiration)
        await loadReport(report.embedUrl, report.accessToken, report.reportId)
      } else {
        setIsLoaded(true)
        navigate(`/m/Report/${reportName}`)
      }
      setIsLoaded(true)
    }
    getReportInfo()
  }, [])

  // Token refresh logic using setTimeout
  useEffect(() => {
    if (refreshTimeoutRef.current !== undefined) {
      clearTimeout(refreshTimeoutRef.current)
    }

    if (!tokenExpiration) return undefined

    const expirationTime = Date.parse(tokenExpiration)
    if (Number.isNaN(expirationTime)) return undefined

    const currentTime = Date.now()
    const bufferTime = MINUTES_BEFORE_EXPIRATION * 60 * 1000
    const rawDelay = expirationTime - currentTime - bufferTime
    const refreshDelay = Math.max(rawDelay, MIN_REFRESH_INTERVAL_MS)

    refreshTimeoutRef.current = window.setTimeout(() => {
      updateToken()
    }, refreshDelay)

    const visibilityHandler = () => {
      if (!document.hidden) {
        const now = Date.now()
        if (now >= expirationTime - bufferTime) {
          updateToken()
        }
      }
    }

    document.addEventListener('visibilitychange', visibilityHandler)

    return () => {
      if (refreshTimeoutRef.current !== undefined) {
        clearTimeout(refreshTimeoutRef.current)
        refreshTimeoutRef.current = undefined
      }
      document.removeEventListener('visibilitychange', visibilityHandler)
    }
  }, [tokenExpiration])

  return (
    <Box id="MainContent" sx={styles.content}>
      {failedToRetrieveReport && (
        <MainContent>
          <Unavailable />
        </MainContent>
      )}

      {!failedToRetrieveReport && (
        <>
          {isAdminUser && (
            <FormControl
              fullWidth={!isMobile}
              style={{
                paddingBottom: embeddedReportRef.current ? '1rem' : '3rem',
              }}>
              <Select
                native
                sx={{ maxWidth: '31.25rem' }}
                id="SelectAdminReport"
                IconComponent={ArrowDownIcon}
                value={adminDropdownValue}
                onChange={handleAdminDropDownChange}
                inputProps={{
                  'aria-label': t('PowerBI.Report.ReportSelect'),
                  'aria-required': true,
                  'data-testid': 'Sel_AdminReport',
                  'aria-describedby': 'adminReportSelectInstructions',
                }}>
                <option value="0" data-testid="Opt_PowerBI_ReportEmpty">
                  {t('PowerBI.Report.ReportSelectPlaceholderText')}
                </option>
                {pbiReportsList != null &&
                  pbiReportsList.length > 0 &&
                  pbiReportsList?.map((report) => (
                    <option
                      key={report.id}
                      value={report.id}
                      data-testid={`Opt_PowerBI_Report_${report.id}`}>
                      {report.displayName}
                    </option>
                  ))}
              </Select>
            </FormControl>
          )}

          {failedToRetrieveNewReport && <Unavailable />}

          {/* Even if the report is unavialble, but admin mode is enabled, report container must exist in order to load a new report
          therefore, in these cases, hide it by setting a height of 0rem */}
          <Spinner open={!isLoaded} />
          {embeddedReportRef.current && (
            <Box sx={styles.fullscreenButtonContainer}>
              <Button
                onClick={handleFullscreen}
                variant="text"
                sx={styles.fullscreenButton}
                data-testid="Btn_PowerBI_Fullscreen"
                startIcon={
                  <FontAwesomeIcon
                    icon={faExpand}
                    style={{ color: 'inherit' }}
                  />
                }>
                {t('PowerBI.Report.FullscreenButton')}
              </Button>
            </Box>
          )}
          <Box sx={getReportContainerStyle()} ref={reportDiv} />
        </>
      )}
    </Box>
  )
}

export default PowerBIReport
